/* eslint-disable no-nested-ternary */
import React, {
  useContext,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { Button, Input, Modal, Tooltip, Tabs, message, DatePicker } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
  PlusCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import RoleChecker, { useCheckRole } from '@components/RoleChecker';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import rolesConstants from '@utils/rolesConstants';
import SessionContext from '@providers/Session/SessionContext';
import SelectCustomers from '@components/SelectCustomers';
import useSearchParams from '@hooks/useSearchParams';
import { useTranslation } from 'react-i18next';
import {
  bytesFormat,
  dateFormat,
  formatPricePerUnit,
  toISOStringWithOffset,
} from '@utils/formatters';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import moment, { Moment } from 'moment';
import CustomTag from '@components/CustomTag';
import NewContractModal from './NewContractModal';

import * as S from './styles';

const { confirm } = Modal;
const { Search } = Input;
const { TabPane } = Tabs;

const Contracts = () => {
  const { t } = useTranslation(['contracts', 'attributes']);
  const { hasCustomersRoles } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const navigate = useNavigate();
  const viewAlerts = useCheckRole({ role: rolesConstants.VIEW_ALERTS });
  const customerType = keycloak?.idTokenParsed?.customerType;
  const isEndCustomer = customerType === 'CUSTOMER';
  const [modalVisible, setModalVisible] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [externalIdValue, setExternalIdValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['showContracted'],
    extraParams: ['customerId', 'externalId', 'description', 'alertDateFrom'],
  });

  const {
    customerId,
    externalId,
    description,
    alertDateFrom,
    showContracted,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const params = useMemo(() => {
    const allFilters = {
      page: page && Number(page) - 1,
      linesPerPage,
      customerId,
      name: description,
      externalId,
      alertDateFrom,
      showContracted,
    };

    allFilters.showContracted = showContracted === 'true';

    if (isEndCustomer) {
      allFilters.showContracted = 'true';
    }

    if (showContracted === 'filed') {
      allFilters.showContracted = 'false';
    }
    return allFilters;
  }, [
    description,
    externalId,
    customerId,
    linesPerPage,
    page,
    showContracted,
    isEndCustomer,
    alertDateFrom,
  ]);

  const { data: contracts, mutate } = useSwr(
    `/service-proxy/broker/virtual-plans`,
    {
      params,
    },
  );

  const { data: archivedContracts, mutate: mutateArchivedContracts } = useSwr(
    !isEndCustomer ? `/service-proxy/broker/virtual-plan/archived` : null,
    {
      params,
    },
  );

  const data = useMemo(
    () => (showContracted === 'filed' ? archivedContracts : contracts),
    [archivedContracts, contracts, showContracted],
  );

  const fileTheContract = useCallback(
    (id: string | number) =>
      api.patch(`/service-proxy/broker/virtual-plan/${id}/archive`),
    [],
  );

  const handleMutateContract = useCallback(
    (contract: any) => {
      mutate({
        ...data,
        content: [contract, ...data.content].slice(0, 10),
      });
    },
    [data, mutate],
  );

  useEffect(() => {
    setExternalIdValue(externalId);
  }, [externalId]);

  useEffect(() => {
    setDescriptionValue(description);
  }, [description]);

  const showArchiveConfirmation = useCallback(
    (contractId: string | number, contractName: string) => {
      confirm({
        title: t('archiveModal.title'),
        icon: <ExclamationCircleOutlined />,
        content: t('archiveModal.alert', { contractName }),
        centered: true,
        okText: t('archiveModal.yes'),
        cancelText: t('archiveModal.no'),
        okButtonProps: { type: 'primary', danger: true },
        afterClose: () => document.body.style.removeProperty('overflow'),
        onOk: () =>
          fileTheContract(contractId)
            .then(() => {
              mutate({
                ...contracts,
                content: contracts?.content?.filter(
                  (item: any) => item.id !== contractId,
                ),
              });

              mutateArchivedContracts({
                ...archivedContracts,
                content: [
                  ...archivedContracts.content.filter(
                    (contract: any) => contract.id !== contractId,
                  ),
                ],
              });
              message.success(t('msgs.successfullyArchived'));
            })
            .catch(({ response: { status } }: { response: any }) => {
              if (status === 409) {
                message.error(t('msgs.errorWhenArchivingWithDependency'));
              } else {
                message.error(t('msgs.errorWhenArchiving'));
              }
            }),
      });
    },
    [
      t,
      mutateArchivedContracts,
      archivedContracts,
      fileTheContract,
      contracts,
      mutate,
    ],
  );

  const adminActions = useCallback(
    (_: any, record: any) => (
      <>
        <Tooltip title={t('buttons.details')}>
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              navigate(`${record.id}`);
            }}
          />
        </Tooltip>
        {showContracted !== 'filed' &&
          showContracted !== 'true' &&
          !isEndCustomer && (
            <RoleChecker role={rolesConstants.MANAGE_CONTRACTS_MOT}>
              <Tooltip title={t('buttons.toFile')}>
                <Button
                  icon={<FolderOpenOutlined />}
                  danger
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    showArchiveConfirmation(record.id, record.planDesc);
                  }}
                />
              </Tooltip>
            </RoleChecker>
          )}
      </>
    ),
    [navigate, t, showContracted, showArchiveConfirmation, isEndCustomer],
  );
  const activatedColumn = useCallback(
    (value: boolean) => {
      const [color, text] = value
        ? ['green', 'activated']
        : ['red', 'disabled'];

      if (value || value === false) {
        return (
          <CustomTag color={color as 'green' | 'red'}>
            {t(`attributes:automaticBlocking.${text}`).toUpperCase()}
          </CustomTag>
        );
      }

      return undefined;
    },

    [t],
  );

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        align: 'center',
        render: (value: string, record: any) => {
          const haveLastAlert =
            Boolean(record.lastAlert) &&
            moment(record.lastAlert).isAfter(moment().subtract(7, 'days'));

          if (haveLastAlert) {
            return (
              <div>
                <Tooltip title={t('attributes:newAlert')}>
                  <Button danger type="link" icon={<WarningOutlined />} />
                </Tooltip>
                <span>{value}</span>
              </div>
            );
          }
          return value;
        },
      },
      {
        title: t('attributes:externalId'),
        dataIndex: 'externalId',
        align: 'center',
      },
      ...(showContracted !== 'true' && !isEndCustomer
        ? [
            {
              title: t('attributes:customer'),
              dataIndex: 'customerId',
              render: (_: any, record: any) => record.customer?.name,
            },
          ]
        : []),
      { title: t('attributes:planDesc'), dataIndex: 'planDesc' },
      {
        title: t('attributes:type.title'),
        dataIndex: 'sharingType',
        align: 'center',
        render: (sharingType: string) => t(`attributes:type.${sharingType}`),
      },
      {
        title: t('attributes:franchise'),
        dataIndex: 'franchise',
        align: 'center',
        render: bytesFormat,
      },
      {
        title: t('attributes:excMbValue'),
        dataIndex: 'megabyteExcPrice',
        align: 'center',
        render: (value: number, record: any) => {
          const toPay = showContracted === 'true';
          const sharingContract = record?.sharingType === 2;

          return (
            (!toPay || (toPay && sharingContract)) && formatPricePerUnit(value)
          );
        },
      },
      {
        title: t('attributes:createdAt'),
        dataIndex: 'createdAt',
        render: (value: string) => dateFormat(value),
      },
      {
        title: t('attributes:vendor'),
        dataIndex: 'motOperators',
        align: 'center',
        render: (value: { name: string }) => value?.name,
      },
      {
        title: t('attributes:planType'),
        dataIndex: 'planType',
        align: 'center',
        render: (value: string) =>
          value && t(`attributes:planTypes.${value.toLowerCase()}`),
      },
      {
        title: t('attributes:planExpirationMonths'),
        dataIndex: 'planExpirationMonths',
        align: 'center',
        render: (value: string) =>
          value && `${value} ${t('attributes:months')}`,
      },
      {
        title: t('attributes:automaticRenewal'),
        dataIndex: 'automaticRenewal',
        align: 'center',
        render: (value: boolean, record: any) =>
          record?.planType !== 'STANDARD' &&
          t(`attributes:hasTag.${value ? 'yes' : 'no'}`),
      },
      {
        title: t('attributes:automaticBlocking.title'),
        dataIndex: 'automaticBlocking',
        align: 'center',
        render: (value: boolean, record: any) => {
          const toPay = showContracted === 'true';
          const sharingContract = record?.sharingType === 2;

          return (
            ((toPay && sharingContract) || !toPay) && activatedColumn(value)
          );
        },
      },
      ...(viewAlerts
        ? [
            {
              title: t('attributes:lastAlertDate'),
              dataIndex: 'lastAlert',
              align: 'center',
              render: (value: string) => value && dateFormat(value),
            },
          ]
        : []),
      ...(showContracted !== 'filled'
        ? [
            {
              title: t('attributes:nbVirtualAccounts'),
              dataIndex: 'nbVirtualAccounts',
              align: 'center',
            },
          ]
        : []),
      {
        title: t('attributes:actions'),
        dataIndex: 'actions',
        align: 'center',
        render: adminActions,
      },
    ],
    [
      t,
      showContracted,
      isEndCustomer,
      viewAlerts,
      adminActions,
      activatedColumn,
    ],
  );

  const ranges = useMemo(
    () => ({
      [t('attributes:lastAlertDateFrom.lastOneHour')]: [
        moment().subtract(1, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastThreeHours')]: [
        moment().subtract(3, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastSixHours')]: [
        moment().subtract(6, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastTwelveHours')]: [
        moment().subtract(12, 'hours'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastDay')]: [
        moment().subtract(1, 'day'),
        moment(),
      ],

      [t('attributes:lastAlertDateFrom.lastWeek')]: [
        moment().subtract(7, 'days'),
        moment(),
      ],
      [t('attributes:lastAlertDateFrom.lastMonth')]: [
        moment().subtract(1, 'months'),
        moment(),
      ],
    }),
    [t],
  );

  const handleDateChange = useCallback(
    (initialDate: Moment | null) => {
      setSearchParams({
        alertDateFrom: initialDate ? toISOStringWithOffset(initialDate) : null,
      });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:customer'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:description'),
        element: (
          <Search
            onSearch={value => setSearchParams({ description: value })}
            onChange={({ target: { value } }) => setDescriptionValue(value)}
            value={descriptionValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:externalId'),
        element: (
          <Search
            onSearch={value => setSearchParams({ externalId: value })}
            onChange={({ target: { value } }) => setExternalIdValue(value)}
            value={externalIdValue}
            allowClear
          />
        ),
      },
      {
        visible: viewAlerts && showContracted !== 'filed',
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:lastAlertDate'),
        element: (
          <DatePicker
            showTime
            value={alertDateFrom && moment(alertDateFrom, 'YYYYMMDD HH:mm:ss')}
            style={{ width: '100%' }}
            format="DD/MM/YYYY HH:mm"
            onChange={handleDateChange}
            allowClear
            renderExtraFooter={() => (
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  flexWrap: 'wrap',
                  padding: '10px 0px 10px 0px',
                }}
              >
                {Object.entries(ranges).map(([label, range]) => (
                  <CustomTag
                    key={label}
                    style={{
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                    onClick={() => handleDateChange(range[0])}
                  >
                    {label}
                  </CustomTag>
                ))}
              </div>
            )}
          />
        ),
      },
    ],
    [
      t,
      setSearchParams,
      hasCustomersRoles,
      customerId,
      descriptionValue,
      externalIdValue,
      showContracted,
      alertDateFrom,
      handleDateChange,
      ranges,
      viewAlerts,
    ],
  );

  return (
    <S.ContractsMotContainer>
      {modalVisible && (
        <RoleChecker role={rolesConstants.MANAGE_CONTRACTS_MOT}>
          <NewContractModal
            visible={modalVisible}
            mutateNewContract={handleMutateContract}
            onClose={() => setModalVisible(false)}
          />
        </RoleChecker>
      )}

      {!isEndCustomer && (
        <Tabs
          type="card"
          defaultActiveKey="false"
          activeKey={showContracted}
          onChange={key => {
            setSearchParams({
              showContracted: key,
            });
          }}
        >
          <TabPane key="false" tab={t('visions.false')} />
          <TabPane key="filed" tab={t('visions.filed')} />
        </Tabs>
      )}

      <Table
        title={t('title')}
        // @ts-ignore
        columns={columns}
        data={data?.content}
        loading={!data?.content}
        key="contractsMot"
        tableKey="contractsMot"
        totalElements={data?.totalElements}
        extraActions={
          <RoleChecker role={rolesConstants.MANAGE_CONTRACTS_MOT}>
            <Tooltip title={t('buttons.newContract')}>
              <Button
                icon={<PlusCircleOutlined />}
                onClick={() => setModalVisible(true)}
              />
            </Tooltip>
          </RoleChecker>
        }
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
      />
    </S.ContractsMotContainer>
  );
};

export default Contracts;
